import { VerifyMethodType } from "shared/types";
import { getValueByPath } from "shared/app/VerificationTemplate/utils";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { VerifiedBankStatement } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import { BankStatementTooltip } from "./BankStatementTooltip";
import { BenefitsStatementTooltip } from "./BenefitsStatementTooltip";
import { BankPayrollTooltip } from "./BankPayrollTooltip";
import { IncomeField, ProviderMap } from "./types";
import { getDisplayedMonthlyDollars, resolveProvider } from "./utils";
import { PaystubTooltip } from "./PaystubTooltip";
import { W2Tooltip } from "./W2Tooltip";

interface VerifiedIncomeTooltipContentProps {
  income: CombinedIncome;
  field: IncomeField;
  incomeSourceProviders?: ProviderMap;
  bankStatements?: Record<number, VerifiedBankStatement>;
  isManualApplicant?: boolean;
}

export const VerifiedIncomeTooltipContent = ({
  income,
  field,
  incomeSourceProviders = {},
  bankStatements = {},
  isManualApplicant = false
}: VerifiedIncomeTooltipContentProps) => {
  const hasFieldOverride =
    typeof getValueByPath(income, [
      "additional_data",
      "overrides",
      field === "gross" ? "gross_monthly_income" : "net_monthly_income"
    ]) === "number";
  if (hasFieldOverride) return <>Manually overridden</>;

  const items = (income.items ?? []).filter((item) => !item.is_archived);
  const provider = resolveProvider(items, incomeSourceProviders);
  const displayedMonthlyAmount = getDisplayedMonthlyDollars(income, field) ?? 0;

  switch (income.verified_source_type) {
    case VerifyMethodType.Paystub:
      return (
        <PaystubTooltip
          items={items}
          field={field}
          provider={provider}
          displayedMonthly={displayedMonthlyAmount}
        />
      );
    case VerifyMethodType.W2:
      return (
        <W2Tooltip
          items={items}
          field={field}
          provider={provider}
          displayedMonthly={displayedMonthlyAmount}
        />
      );
    case VerifyMethodType.BankStatement:
      return (
        <BankStatementTooltip
          income={income}
          items={items}
          field={field}
          bankStatements={bankStatements}
          displayedMonthly={displayedMonthlyAmount}
          isManualApplicant={isManualApplicant}
        />
      );
    case VerifyMethodType.BenefitsStatement:
      return (
        <BenefitsStatementTooltip
          items={items}
          field={field}
          displayedMonthly={displayedMonthlyAmount}
          isManualApplicant={isManualApplicant}
        />
      );
    case VerifyMethodType.Bank:
    case VerifyMethodType.Payroll:
      return (
        <BankPayrollTooltip
          provider={provider}
          verifyMethod={
            income.verified_source_type === VerifyMethodType.Payroll
              ? VerifyMethodType.Payroll
              : VerifyMethodType.Bank
          }
          field={field}
        />
      );
    default:
      return null;
  }
};
